import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import MenuLateral from '../../Components/MenuPrincipal/MenuLateral';
import { useAuth } from '../../Context/auth';
import './../../Empresas/formulario.css'; // Css Padrão

function ClienteForm() {
    const { id } = useParams(); // Se tiver ID, é edição
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    // Estados do Formulário
    const [formData, setFormData] = useState({
        nome: '',
        email: '',
        cpf_cnpj: '',
        telefone: '',
        cep: '',
        cidade: '',
        uf: '',
        status: 1, // 1 Ativo
        data_vencimento: '',
        firebase_uid: '', // Apenas Criação
        plano: 'Produção', // Default
        max_sessions: 3,

        valor: '', // Novo campo
        qt_usuarios: 0,
        data_cadastro: ''
    });

    const [sessions, setSessions] = useState([]);
    const [modulosDisponiveis, setModulosDisponiveis] = useState([]); // [NOVO]
    const [modulosSelecionados, setModulosSelecionados] = useState([]); // [NOVO]
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(!!id);

    const carregarDados = React.useCallback(async (clienteId) => {
        setLoadingData(true);
        try {
            const token = await currentUser.getIdToken();
            const API_URL = process.env.REACT_APP_API_URL;
            const res = await fetch(`${API_URL}/api/gerencial/clientes/${clienteId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Falha ao carregar cliente');
            const data = await res.json();

            // Ajustar formato de data se necessário (pra input date)
            let vencimento = '';
            if (data.data_vencimento) {
                vencimento = data.data_vencimento.split('T')[0];
            }

            setFormData({
                ...data,
                status: data.status || 1,
                data_vencimento: vencimento,
                max_sessions: data.max_sessions || 3,
                valor: data.valor || '',
                plano: data.plano || 'Produção',
                qt_usuarios: data.qt_usuarios || 0,
                qt_sessoes_ativas: data.qt_sessoes_ativas || 0,
                ultimo_login: data.ultimo_login || '',
                data_cadastro: data.created_at || data.data_cadastro // Tenta pegar created_at ou data_cadastro
            });

            // [NOVO] Setar módulos selecionados
            if (data.modulos && Array.isArray(data.modulos)) {
                // Força conversão para números para evitar bugs de tipo
                setModulosSelecionados(data.modulos.map(m => Number(m)));
            } else {
                setModulosSelecionados([]);
            }

            // Fetch Sessions
            const sessionsRes = await fetch(`${API_URL}/api/gerencial/clientes/${clienteId}/sessions`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (sessionsRes.ok) {
                const sessionsData = await sessionsRes.json();
                setSessions(sessionsData);
            }
        } catch (error) {
            console.error(error);
            Swal.fire('Erro', 'Não foi possível carregar os dados.', 'error');
            navigate('/app/gerencial/clientes');
        } finally {
            setLoadingData(false);
        }
    }, [currentUser, navigate]);

    useEffect(() => {
        if (id && currentUser) {
            carregarDados(id);
        }
    }, [id, currentUser, carregarDados]);

    // [NOVO] Buscar módulos disponíveis
    useEffect(() => {
        const fetchModulos = async () => {
            if (!currentUser) return;
            try {
                const token = await currentUser.getIdToken();
                const API_URL = process.env.REACT_APP_API_URL;
                const res = await fetch(`${API_URL}/api/modulos`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setModulosDisponiveis(data);
                }
            } catch (error) {
                console.error("Erro ao carregar módulos:", error);
            }
        };
        fetchModulos();
    }, [currentUser]);

    // [NOVO] Handler para checkboxes
    const handleModuloChange = (moduloId) => {
        const idNum = Number(moduloId);
        setModulosSelecionados(prev => {
            if (prev.includes(idNum)) {
                return prev.filter(id => id !== idNum);
            } else {
                return [...prev, idNum];
            }
        });
    };

    const handleKillSession = async (sessionId, userName) => {
        const confirm = await Swal.fire({
            title: 'Tem certeza?',
            text: `Deseja derrubar a sessão de ${userName}? O usuário será desconectado imediatamente.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sim, derrubar!',
            cancelButtonText: 'Cancelar'
        });

        if (confirm.isConfirmed) {
            try {
                const token = await currentUser.getIdToken();
                const API_URL = process.env.REACT_APP_API_URL;
                const res = await fetch(`${API_URL}/api/gerencial/sessions/${sessionId}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!res.ok) throw new Error('Falha ao encerrar sessão.');

                Swal.fire('Sucesso!', 'Sessão encerrada.', 'success');

                // Refresh sessions
                setSessions(prev => prev.filter(s => s.id !== sessionId));
                setFormData(prev => ({ ...prev, qt_sessoes_ativas: (prev.qt_sessoes_ativas - 1) }));

            } catch (error) {
                console.error(error);
                Swal.fire('Erro', 'Não foi possível encerrar a sessão.', 'error');
            }
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const token = await currentUser.getIdToken();
            const API_URL = process.env.REACT_APP_API_URL;

            const url = id
                ? `${API_URL}/api/gerencial/clientes/${id}`
                : `${API_URL}/api/gerencial/clientes`;

            const method = id ? 'PUT' : 'POST';

            // Garantir que status tenha valor
            const payload = {
                ...formData,
                status: formData.status || 1,
                modulos: modulosSelecionados // [NOVO]
            };

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.error || 'Erro ao salvar');
            }

            await Swal.fire('Sucesso!', 'Cliente salvo com sucesso.', 'success');
            navigate('/app/gerencial/clientes');

        } catch (error) {
            Swal.fire('Erro', error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="form-page-container" style={{ display: 'flex', paddingTop: 0 }}>
            <MenuLateral />

            <div className="content-with-sidebar">

                <div className="page-header page-header-compact d-flex justify-content-between align-items-center">
                    <h1 className="page-title page-title-sm">
                        {id ? 'Editar' : 'Novo'} <span className="highlight">Cliente (Tenant)</span>
                    </h1>
                    {id && (
                        <button className="btn btn-light btn-sm border" onClick={() => carregarDados(id)} title="Atualizar Dados e Sessões">
                            <i className={`fas fa-sync-alt me-2 ${loadingData ? 'fa-spin' : ''}`}></i> Atualizar
                        </button>
                    )}
                </div>

                <div className="form-card-container">
                    <form onSubmit={handleSubmit} className="w-100 mb-5">
                        <div className="row g-4">
                            {/* Coluna Principal */}
                            <div className="col-lg-8">
                                <div className="form-card">
                                    <h2 className="form-section-header">Dados Gerais</h2>

                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label-custom required-field">Nome / Razão Social</label>
                                            <input type="text" className="form-control-custom" name="nome" value={formData.nome} onChange={handleChange} required />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label-custom required-field">E-mail (Login Admin)</label>
                                            <input type="email" className="form-control-custom" name="email" value={formData.email} onChange={handleChange} required />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label-custom required-field">CPF / CNPJ</label>
                                            <input type="text" className="form-control-custom" name="cpf_cnpj" value={formData.cpf_cnpj} onChange={handleChange} required />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label-custom">Telefone</label>
                                            <input type="text" className="form-control-custom" name="telefone" value={formData.telefone} onChange={handleChange} />
                                        </div>

                                        {!id && (
                                            <div className="col-12">
                                                <label className="form-label-custom required-field">Firebase UID (Usuário Admin)</label>
                                                <input type="text" className="form-control-custom" name="firebase_uid" value={formData.firebase_uid} onChange={handleChange} placeholder="UID do usuário no Firebase Auth" required />
                                                <small className="text-muted mt-1 d-block">Crie o usuário no Firebase Console e cole o UID aqui.</small>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="form-card mt-4">
                                    <h2 className="form-section-header">Endereço</h2>
                                    <div className="row g-3">
                                        <div className="col-md-3">
                                            <label className="form-label-custom">CEP</label>
                                            <input type="text" className="form-control-custom" name="cep" value={formData.cep} onChange={handleChange} />
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label-custom">UF</label>
                                            <input type="text" className="form-control-custom" name="uf" value={formData.uf} onChange={handleChange} maxLength="2" />
                                        </div>
                                        <div className="col-md-7">
                                            <label className="form-label-custom">Cidade</label>
                                            <input type="text" className="form-control-custom" name="cidade" value={formData.cidade} onChange={handleChange} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Coluna Lateral */}
                            <div className="col-lg-4">
                                <div className="form-card">
                                    <h2 className="form-section-header">Contrato</h2>

                                    <div className="d-flex flex-column gap-3">
                                        <div>
                                            <label className="form-label-custom">Status</label>
                                            <select className="form-select-custom" name="status" value={formData.status} onChange={handleChange}>
                                                <option value="1">Ativo</option>
                                                <option value="2">Pendente/Teste</option>
                                                <option value="3">Vencido/Bloq</option>
                                                <option value="4">Inativo</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="form-label-custom">Data de Cadastro</label>
                                            <input
                                                type="text"
                                                className="form-control-custom"
                                                value={formData.data_cadastro ? new Date(formData.data_cadastro).toLocaleDateString() : '-'}
                                                disabled
                                                readOnly
                                            />
                                        </div>



                                        <div>
                                            <label className="form-label-custom required-field">Licenças (Sessões)</label>
                                            <input type="number" className="form-control-custom" name="max_sessions" value={formData.max_sessions} onChange={handleChange} min="1" required />
                                            <small className="text-muted">Max. sessões simultâneas</small>
                                        </div>

                                        <div>
                                            <label className="form-label-custom">Valor (R$)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className="form-control-custom"
                                                name="valor"
                                                value={formData.valor}
                                                onChange={handleChange}
                                                placeholder="0,00"
                                            />
                                        </div>

                                        <div>
                                            <label className="form-label-custom required-field">Próximo Vencimento</label>
                                            <input type="date" className="form-control-custom" name="data_vencimento" value={formData.data_vencimento} onChange={handleChange} required />
                                        </div>
                                    </div>

                                    {/* [NOVO] Seção de Módulos */}
                                    <div>
                                        <label className="form-label-custom fw-bold border-bottom pb-1 mb-2 d-block">Módulos Contratados</label>
                                        <div className="d-flex flex-column gap-2 p-2 rounded bg-light border">
                                            {modulosDisponiveis.map(mod => (
                                                <div key={mod.id} className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`mod-${mod.id}`}
                                                        checked={modulosSelecionados.includes(Number(mod.id))}
                                                        onChange={() => handleModuloChange(mod.id)}
                                                    />
                                                    <label className="form-check-label user-select-none" htmlFor={`mod-${mod.id}`} style={{ cursor: 'pointer' }}>
                                                        {mod.nome} <span className="text-muted small">({Number(mod.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })})</span>
                                                    </label>
                                                </div>
                                            ))}
                                            {modulosDisponiveis.length === 0 && <small className="text-muted">Nenhum módulo disponível.</small>}
                                        </div>
                                    </div>
                                </div>

                                <div className="form-card mt-4">
                                    <h2 className="form-section-header">Acompanhamento</h2>
                                    <div className="d-flex flex-column gap-3">
                                        <div>
                                            <label className="form-label-custom">Usuários Cadastrados</label>
                                            <input type="text" className="form-control-custom" value={formData.qt_usuarios} disabled readOnly />
                                        </div>
                                        <div>
                                            <label className="form-label-custom">Sessões Ativas (Total)</label>
                                            <input type="text" className="form-control-custom" value={formData.qt_sessoes_ativas || 0} disabled readOnly />
                                        </div>
                                        <div>
                                            <label className="form-label-custom">Último Login</label>
                                            <input
                                                type="text"
                                                className="form-control-custom"
                                                value={formData.ultimo_login ? new Date(formData.ultimo_login).toLocaleString() : '-'}
                                                disabled
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Card de Sessões Ativas */}
                        {id && (
                            <div className="row g-4 mt-1">
                                <div className="col-12">
                                    <div className="form-card">
                                        <h2 className="form-section-header">Gerenciamento de Sessões Ativas</h2>

                                        {sessions.length === 0 ? (
                                            <div className="alert alert-info">Nenhuma sessão ativa encontrada.</div>
                                        ) : (
                                            <div className="table-responsive">
                                                <table className="table table-hover">
                                                    <thead>
                                                        <tr>
                                                            <th>Usuário</th>
                                                            <th>IP</th>
                                                            <th>Início</th>
                                                            <th>Última Ativ.</th>
                                                            <th>Ações</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {sessions.map(sess => (
                                                            <tr key={sess.id}>
                                                                <td>
                                                                    <div><strong>{sess.user_nome}</strong></div>
                                                                    <small className="text-muted">{sess.user_email}</small>
                                                                </td>
                                                                <td>{sess.ip_address}</td>
                                                                <td>{new Date(sess.created_at).toLocaleString()}</td>
                                                                <td>{new Date(sess.last_activity).toLocaleString()}</td>
                                                                <td>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-danger"
                                                                        onClick={() => handleKillSession(sess.id, sess.user_nome)}
                                                                    >
                                                                        Derrubar
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="form-actions-bar">
                            <div className="container-fluid d-flex justify-content-end gap-3 align-items-center">
                                <button type="button" className="btn btn-secondary-custom" onClick={() => navigate('/app/gerencial/clientes')}>Cancelar</button>
                                <button type="submit" className="btn btn-primary-custom" disabled={loading}>
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Salvando...
                                        </>
                                    ) : (
                                        'Salvar Cliente'
                                    )}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default ClienteForm;
